// frontend/src/site/templates/TemplateCorretorLovable/components/PropertiesSection.jsx
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, ArrowRight } from 'lucide-react';

import { Button } from '@/site/templates/TemplateCorretorLovable/ui/button';

function getImageUrl(img) {
  if (!img) return null;
  if (typeof img === 'string') return img;

  if (typeof img === 'object') {
    return (
      img.url || img.src || img.downloadURL || img.fileUrl || img.link || null
    );
  }

  return null;
}

function pickMainImage(p = {}) {
  const fromSingle =
    getImageUrl(p.thumbnail) ||
    getImageUrl(p.capaUrl) ||
    getImageUrl(p.fotoDestaque) ||
    getImageUrl(p.capa) ||
    getImageUrl(p.capaUrlMobile) ||
    getImageUrl(p.thumbnailUrl) ||
    null;

  if (fromSingle) return fromSingle;

  const arr = Array.isArray(p.fotos)
    ? p.fotos
    : Array.isArray(p.imagens)
      ? p.imagens
      : Array.isArray(p.images)
        ? p.images
        : [];

  for (const it of arr) {
    const u = getImageUrl(it);
    if (u) return u;
  }

  return null;
}

const formatBRL = (value) => {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return 'R$ 0';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

function normalizeProperty(p = {}) {
  const id = p.id || p.objectID || p.uid || p.refCodigo || p.ref || '';

  const statusRaw = String(p.status || p.negocioTipo || p.finalidade || '')
    .toLowerCase()
    .trim();

  const status =
    statusRaw.includes('loc') || statusRaw.includes('alug')
      ? 'Locação'
      : 'Venda';

  const price = Number(p.preco ?? p.price ?? 0);
  const title = p.titulo || p.title || p.tipo || p.type || p.nome || 'Imóvel';

  const neighborhood = p.bairro || p.neighborhood || '';
  const city = p.cidade || p.city || '';
  const location =
    p.location ||
    [neighborhood, city].filter(Boolean).join(', ') ||
    city ||
    '—';

  const beds = Number(p.dormitorios ?? p.beds ?? 0);
  const baths = Number(p.banheiros ?? p.baths ?? 0);

  const area =
    Number(p.areaConstruida ?? p.area ?? p.m2 ?? 0) ||
    Number(p.areaTerreno ?? 0);

  const type = p.tipo || p.type || 'Imóvel';
  const featured = Boolean(p.destaque || p.featured);
  const image = pickMainImage(p);

  return {
    id: String(id || ''),
    image,
    title,
    location,
    neighborhood: neighborhood || '—',
    city: city || '—',
    price,
    priceFormatted: formatBRL(price),
    beds,
    baths,
    area,
    type,
    status,
    featured,
    raw: p,
  };
}

function getBasePath({ handle, slug, pathname }) {
  const path = String(pathname || '').toLowerCase();
  if (path.startsWith('/u/')) {
    const h = String(handle || slug || '').trim();
    return h ? `/u/${h}` : '/u';
  }
  const s = String(slug || handle || '').trim();
  return s ? `/site/${s}` : '/site';
}

function asObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}
function asArr(v) {
  return Array.isArray(v) ? v : [];
}

function resolveFeaturedConfig(conteudo) {
  const c = asObj(conteudo);

  const im = asObj(c.imoveis);
  const idsIm = asArr(im.ids).filter(Boolean).map(String);

  const des = asObj(c.destaques);
  const idsDes = asArr(des.ids).filter(Boolean).map(String);

  const ids = idsIm.length ? idsIm : idsDes;

  const quantidadeRaw =
    Number(im.quantidade || 0) || Number(des.quantidade || 0) || 6;
  const quantidade = Math.max(1, Math.min(quantidadeRaw, 6));

  const modoRaw = String(im.modo || des.modo || '').toLowerCase();
  const modo = ids.length > 0 ? 'selecionados' : modoRaw || 'recentes';

  const titulo =
    String(im.titulo || des.titulo || '').trim() || 'Imóveis em Destaque';

  const subtitulo =
    String(im.subtitulo || des.subtitulo || '').trim() ||
    'Seleção exclusiva de imóveis que atendem aos mais altos padrões de qualidade e localização.';

  const mostrarBotaoVerTodos =
    im.mostrarBotaoVerTodos ?? des.mostrarBotaoVerTodos ?? true;

  const textoBotaoVerTodos =
    String(im.textoBotaoVerTodos || des.textoBotaoVerTodos || '').trim() ||
    'Ver todos os imóveis';

  return {
    ids,
    quantidade,
    modo,
    titulo,
    subtitulo,
    mostrarBotaoVerTodos,
    textoBotaoVerTodos,
  };
}

export default function PropertiesSection({
  siteConfig,
  properties = [],
  slug,
  handle,
  conteudo,
}) {
  const { pathname } = useLocation();

  const basePath = useMemo(
    () => getBasePath({ handle, slug, pathname }),
    [handle, slug, pathname]
  );

  const normalized = useMemo(
    () => (Array.isArray(properties) ? properties : []).map(normalizeProperty),
    [properties]
  );

  const cfg = useMemo(() => resolveFeaturedConfig(conteudo), [conteudo]);

  const featured = useMemo(() => {
    if (cfg.modo === 'selecionados' && cfg.ids.length) {
      const byId = new Map(normalized.map((p) => [String(p.id), p]));
      const picked = cfg.ids.map((id) => byId.get(String(id))).filter(Boolean);
      return picked.slice(0, cfg.quantidade);
    }

    const listFeatured = normalized.filter((p) => p.featured);
    const base = listFeatured.length ? listFeatured : normalized;
    return base.slice(0, cfg.quantidade);
  }, [cfg.ids, cfg.modo, cfg.quantidade, normalized]);

  return (
    <section className="py-16 lg:py-20 bg-beige-dark">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div className="max-w-xl">
            <p className="text-xs tracking-[0.3em] text-muted-foreground mb-3">
              PORTFÓLIO
            </p>

            <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-foreground mb-4">
              {cfg.titulo}
            </h2>

            <p className="text-muted-foreground leading-relaxed">
              {cfg.subtitulo}
            </p>
          </div>

          {cfg.mostrarBotaoVerTodos && (
            <Link to={`${basePath}/imoveis`}>
              <Button variant="goldOutline" className="gap-2">
                {cfg.textoBotaoVerTodos} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          )}
        </div>

        {featured.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <article
                key={p.id}
                className="group bg-card rounded-2xl overflow-hidden border border-border hover:border-gold/30 transition-all duration-300 shadow-card hover:shadow-medium"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <span className="text-muted-foreground text-sm">
                        Sem imagem
                      </span>
                    </div>
                  )}

                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                      {p.type}
                    </span>

                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        p.status === 'Venda'
                          ? 'bg-gold text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground'
                      }`}
                    >
                      {p.status}
                    </span>

                    {cfg.modo === 'selecionados' && (
                      <span className="px-3 py-1 bg-foreground text-background text-xs font-medium rounded-full">
                        Destaque
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <p className="font-serif text-2xl font-semibold text-gold">
                      {p.priceFormatted}
                    </p>

                    <h3 className="font-serif text-xl font-semibold text-foreground mt-1">
                      {p.title}
                    </h3>

                    <div className="flex items-center gap-1 text-muted-foreground text-sm mt-2">
                      <MapPin className="w-4 h-4" />
                      {p.location}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-1 text-muted-foreground text-sm">
                      <Bed className="w-4 h-4" />
                      <span>{p.beds} quartos</span>
                    </div>

                    <div className="flex items-center gap-1 text-muted-foreground text-sm">
                      <Bath className="w-4 h-4" />
                      <span>{p.baths} banheiros</span>
                    </div>

                    <div className="flex items-center gap-1 text-muted-foreground text-sm">
                      <Square className="w-4 h-4" />
                      <span>{p.area ? `${p.area}m²` : '—'}</span>
                    </div>
                  </div>

                  <Link to={`${basePath}/imovel/${p.id}`}>
                    <Button variant="ghost" size="sm" className="w-full">
                      Ver detalhes
                    </Button>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Nenhum imóvel encontrado.</p>
          </div>
        )}
      </div>
    </section>
  );
}
